// src/pages/AddGenericBookPage.js

import React, { useState, useEffect } from 'react';
import { Box, Button, LinearProgress, TextField, Typography, MenuItem, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance';
import Papa from 'papaparse';

const AddGenericBookPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    bookTitle: '',
    bookDescription: '',
    authorName: '',
    publisherName: '',
    publicationYear: '',
    publisherEdition: '',
    ISBN: '',
    bookType: '',
    bookLanguage: '',
    coverImageURL: '',
    pdfURL: '',
    courseId: '',
    subjectId: '',
    semester: ''
  });

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    axios.get('https://backend.scholarkart.in/api/courses').then((res) => setCourses(res.data));
  }, []);

  useEffect(() => {
    if (formData.courseId) {
      axios.get(`https://backend.scholarkart.in/api/subjects?courseId=${formData.courseId}`).then((res) => setSubjects(res.data));
    } else {
      setSubjects([]);
    }
  }, [formData.courseId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.bookTitle || !formData.authorName || !formData.bookType || !formData.bookLanguage) {
      alert('Please fill all required fields.');
      return;
    }

    const courseDetails = {};
    if (formData.courseId) courseDetails.courseId = formData.courseId;
    if (formData.subjectId) courseDetails.subjectId = formData.subjectId;
    if (formData.semester) courseDetails.semester = formData.semester;

    const bookPayload = {
      ...formData,
      courseDetails
    };

    delete bookPayload.courseId;
    delete bookPayload.subjectId;
    delete bookPayload.semester;

    try {
      await axios.post('https://backend.scholarkart.in/api/books/generic', bookPayload); // No collegeId!
      alert('✅ Book added successfully (Generic)');
      navigate('/librarymanagement');
    } catch (error) {
      console.error('Error adding book:', error);
      alert('❌ Failed to add book');
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rawBooks = results.data;
        const validBooks = [];
  
        rawBooks.forEach((row) => {
          if (!row.bookTitle || !row.authorName || !row.bookType || !row.bookLanguage) return;
  
          const courseDetails = {};
          if (row.courseId) courseDetails.courseId = row.courseId;
          if (row.subjectId) courseDetails.subjectId = row.subjectId;
          if (row.semester) courseDetails.semester = row.semester;
  
          validBooks.push({ ...row, courseDetails });
        });
  
        try {
          setUploading(true);
          setProgress(10);
  
          await axios.post('/api/books/generic/bulk', { books: validBooks }, {
            onUploadProgress: (event) => {
              const percent = Math.round((event.loaded * 100) / event.total);
              setProgress(percent);
            }
          });
  
          setProgress(100);
          alert('📚 Generic books uploaded successfully!');
        } catch (err) {
          console.error('Bulk import error:', err);
          alert('❌ Bulk import failed');
        } finally {
          setTimeout(() => {
            setUploading(false);
            setProgress(0);
          }, 1000);
        }
      }
    });
  };
  
  

  return (
    <Box p={4}>
      <Typography variant="h4" mb={4}>Add Public/Generic Book</Typography>
      <Button
        variant="outlined"
        component="label"
        sx={{ mt: 3 }}
      >
        Bulk Upload (.csv)
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleCSVUpload}
        />
      </Button>
      <Button
        variant="outlined"
        sx={{ mt: 3, mr: 2 }}
        onClick={() => {
          const headers = [
            'bookTitle',
            'bookDescription',
            'authorName',
            'publisherName',
            'publicationYear',
            'publisherEdition',
            'ISBN',
            'bookType',
            'bookLanguage',
            'coverImageURL',
            'pdfURL',
            'courseId',
            'subjectId',
            'semester'
          ];
          const csvContent = headers.join(',') + '\n';
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "generic_book_template.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      >
        📥 Download CSV Template
      </Button>
      {uploading && (
        <Box mt={2}>
          <LinearProgress variant="determinate" value={progress} />
          <p className="text-sm text-gray-600 mt-1">Uploading... {progress}%</p>
        </Box>
      )}
      <br /><br />
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Book Title" name="bookTitle" value={formData.bookTitle} onChange={handleChange} fullWidth required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Author Name" name="authorName" value={formData.authorName} onChange={handleChange} fullWidth required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Publisher Name" name="publisherName" value={formData.publisherName} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Publication Year" name="publicationYear" value={formData.publicationYear} onChange={handleChange} fullWidth type="number" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Edition" name="publisherEdition" value={formData.publisherEdition} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="ISBN" name="ISBN" value={formData.ISBN} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Book Type" name="bookType" value={formData.bookType} onChange={handleChange} fullWidth required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Language" name="bookLanguage" value={formData.bookLanguage} onChange={handleChange} fullWidth required />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Book Description" name="bookDescription" value={formData.bookDescription} onChange={handleChange} fullWidth multiline rows={3} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Cover Image URL" name="coverImageURL" value={formData.coverImageURL} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="PDF URL" name="pdfURL" value={formData.pdfURL} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Course"
              name="courseId"
              value={formData.courseId}
              onChange={handleChange}
              fullWidth
            >
              {courses.map((course) => (
                <MenuItem key={course._id} value={course._id}>{course.courseName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Subject"
              name="subjectId"
              value={formData.subjectId}
              onChange={handleChange}
              fullWidth
            >
              {subjects.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>{subject.subjectName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              fullWidth
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                <MenuItem key={sem} value={sem}>Semester {sem}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary">Add Generic Book</Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddGenericBookPage;
